/**
 * live.gesture — MediaPipe Tasks Vision Gesture Recognizer (Apache-2.0).
 * Runs fully in-browser; no suite debit.
 *
 * Docs: https://developers.google.com/edge/mediapipe/solutions/vision/gesture_recognizer
 */

import type { EngineContext, VisionEngine, VisionEvent } from "./types";
import { matchCustomGesture } from "../actions/customGestures";

const WASM_CDN =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/gesture_recognizer/gesture_recognizer/float16/1/gesture_recognizer.task";

/** MediaPipe built-in gesture names (plus derived Pinch). Custom names live in customGestures store. */
export const KNOWN_GESTURES = [
  "None",
  "Closed_Fist",
  "Open_Palm",
  "Pointing_Up",
  "Thumb_Down",
  "Thumb_Up",
  "Victory",
  "ILoveYou",
  "Pinch",
] as const;

const BUILTIN = new Set<string>(KNOWN_GESTURES);

type GestureRecognizerLike = {
  recognizeForVideo: (
    video: HTMLVideoElement,
    timestamp: number,
  ) => {
    gestures?: { categoryName: string; score: number }[][];
    landmarks?: { x: number; y: number; z?: number }[][];
  };
  close?: () => void;
};

function attachStream(el: HTMLVideoElement | undefined, stream: MediaStream): void {
  if (!el) return;
  el.srcObject = stream;
  el.playsInline = true;
  el.muted = true;
  el.autoplay = true;
  void el.play().catch(() => undefined);
}

export function createGestureEngine(): VisionEngine {
  let running = false;
  let stream: MediaStream | null = null;
  let recognizer: GestureRecognizerLike | null = null;
  let raf = 0;
  let tickTimer: ReturnType<typeof setInterval> | null = null;
  let visHandler: (() => void) | null = null;
  let lastVideoTime = -1;
  let lastEmitName = "";
  let lastEmitAt = 0;
  let fpsFrames = 0;
  let fpsLast = performance.now();
  let fps = 0;
  let ctxRef: EngineContext | null = null;
  let lastStatus = "idle";

  const emit = (e: VisionEvent) => {
    ctxRef?.onEvent?.(e);
  };

  const drawLandmarks = (
    canvas: HTMLCanvasElement,
    video: HTMLVideoElement,
    hands: { x: number; y: number }[][],
  ) => {
    const w = video.videoWidth || canvas.clientWidth;
    const h = video.videoHeight || canvas.clientHeight;
    if (!w || !h) return;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const c = canvas.getContext("2d");
    if (!c) return;
    c.clearRect(0, 0, w, h);
    // Mirror to match CSS transform on video
    c.save();
    c.scale(-1, 1);
    c.translate(-w, 0);
    c.strokeStyle = "rgba(34, 211, 238, 0.9)";
    c.fillStyle = "rgba(34, 211, 238, 0.95)";
    c.lineWidth = 2;
    const connections: [number, number][] = [
      [0, 1], [1, 2], [2, 3], [3, 4],
      [0, 5], [5, 6], [6, 7], [7, 8],
      [5, 9], [9, 10], [10, 11], [11, 12],
      [9, 13], [13, 14], [14, 15], [15, 16],
      [13, 17], [0, 17], [17, 18], [18, 19], [19, 20],
    ];
    for (const pts of hands) {
      for (const [a, b] of connections) {
        const pa = pts[a];
        const pb = pts[b];
        if (!pa || !pb) continue;
        c.beginPath();
        c.moveTo(pa.x * w, pa.y * h);
        c.lineTo(pb.x * w, pb.y * h);
        c.stroke();
      }
      for (const p of pts) {
        c.beginPath();
        c.arc(p.x * w, p.y * h, 3.5, 0, Math.PI * 2);
        c.fill();
      }
    }
    c.restore();
  };

  /** Pinch = thumb tip near index tip (landmarks 4 & 8). */
  const detectPinch = (pts: { x: number; y: number }[]): number | null => {
    const t = pts[4];
    const i = pts[8];
    if (!t || !i) return null;
    const d = Math.hypot(t.x - i.x, t.y - i.y);
    if (d < 0.06) return Math.min(1, 1 - d / 0.06);
    return null;
  };

  const sourceVideo = (): HTMLVideoElement | null => {
    return ctxRef?.processVideoEl || ctxRef?.videoEl || null;
  };

  const tick = () => {
    if (!running || !recognizer) return;
    const video = sourceVideo();
    if (!video) return;
    const canvas = ctxRef?.canvasEl;
    if (video.paused) void video.play().catch(() => undefined);
    if (video.readyState >= 2 && video.currentTime !== lastVideoTime) {
      lastVideoTime = video.currentTime;
      const now = performance.now();
      try {
        const result = recognizer.recognizeForVideo(video, now);
        const hands = result.landmarks || [];
        const gestures = result.gestures || [];

        if (canvas && hands.length) {
          drawLandmarks(
            canvas,
            video,
            hands.map((h) => h.map((p) => ({ x: p.x, y: p.y }))),
          );
        } else if (canvas) {
          const c = canvas.getContext("2d");
          c?.clearRect(0, 0, canvas.width, canvas.height);
        }

        let name = "None";
        let confidence = 0;
        if (gestures[0]?.[0]) {
          name = gestures[0][0].categoryName || "None";
          confidence = gestures[0][0].score ?? 0;
        }
        // Prefer derived Pinch when strong
        if (hands[0]) {
          const pinch = detectPinch(hands[0]);
          if (pinch != null && pinch > 0.55 && (name === "None" || confidence < 0.6)) {
            name = "Pinch";
            confidence = pinch;
          }
        }
        // Taught poses win when the template is a tight match
        if (hands[0]) {
          const custom = matchCustomGesture(hands[0]);
          if (custom) {
            const builtinWeak =
              name === "None" ||
              !BUILTIN.has(name) ||
              confidence < 0.72 ||
              custom.confidence >= 0.82;
            if (builtinWeak && custom.confidence >= 0.58) {
              name = custom.name;
              confidence = custom.confidence;
            }
          }
        }

        if (hands[0]) {
          emit({
            type: "landmarks",
            points: hands[0].map((p) => [p.x, p.y, p.z ?? 0]),
            t: now,
          });
        }

        // Debounce same gesture (except None)
        const changed = name !== lastEmitName;
        const cooled = now - lastEmitAt > 350;
        if (name !== "None" && confidence >= 0.45 && (changed || cooled)) {
          lastEmitName = name;
          lastEmitAt = now;
          emit({
            type: "gesture",
            name,
            confidence,
            hands: hands.length,
            t: now,
          });
        } else if (name === "None" && lastEmitName !== "None" && cooled) {
          lastEmitName = "None";
        }

        fpsFrames++;
        if (now - fpsLast >= 1000) {
          fps = fpsFrames;
          fpsFrames = 0;
          fpsLast = now;
          emit({
            type: "status",
            message: `live · ${fps} fps · ${name} ${confidence ? (confidence * 100).toFixed(0) + "%" : ""}`.trim(),
            level: "info",
          });
        }
      } catch (err) {
        emit({
          type: "status",
          message: err instanceof Error ? err.message : String(err),
          level: "error",
        });
      }
    }
  };

  const loop = () => {
    tick();
    if (running) raf = requestAnimationFrame(loop);
  };

  return {
    id: "gesture",
    kind: "live",
    label: "Live hands",
    description: "Webcam hand tracking + gestures — free in-browser path. No suite debit.",
    status: "ready",

    async start(ctx: EngineContext) {
      if (running) this.stop();
      ctxRef = ctx;
      running = true;
      lastStatus = "starting";
      emit({ type: "status", message: "Requesting camera…", level: "info" });

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (e) {
        running = false;
        lastStatus = "camera denied";
        emit({
          type: "status",
          message: e instanceof Error ? e.message : "Camera permission denied",
          level: "error",
        });
        throw e;
      }

      attachStream(ctx.processVideoEl, stream);
      attachStream(ctx.videoEl, stream);
      if (ctx.processVideoEl && ctx.processVideoEl !== ctx.videoEl) {
        await ctx.processVideoEl.play().catch(() => undefined);
      }

      emit({ type: "status", message: "Loading live hand engine…", level: "info" });

      const vision = await import("@mediapipe/tasks-vision");
      const { FilesetResolver, GestureRecognizer } = vision;
      const fileset = await FilesetResolver.forVisionTasks(WASM_CDN);
      recognizer = (await GestureRecognizer.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: MODEL_URL,
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numHands: 2,
      })) as GestureRecognizerLike;

      lastStatus = "running";
      emit({ type: "status", message: "Live hands running · free", level: "info" });
      raf = requestAnimationFrame(loop);
      if (tickTimer) clearInterval(tickTimer);
      tickTimer = setInterval(tick, 33);
      visHandler = () => {
        if (document.visibilityState !== "visible") return;
        const v = sourceVideo();
        if (v) void v.play().catch(() => undefined);
        if (ctxRef?.videoEl && ctxRef.videoEl !== v) void ctxRef.videoEl.play().catch(() => undefined);
      };
      document.addEventListener("visibilitychange", visHandler);
    },

    stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      if (tickTimer) {
        clearInterval(tickTimer);
        tickTimer = null;
      }
      if (visHandler) {
        document.removeEventListener("visibilitychange", visHandler);
        visHandler = null;
      }
      try {
        recognizer?.close?.();
      } catch {
        /* ignore */
      }
      recognizer = null;
      if (stream) {
        for (const t of stream.getTracks()) t.stop();
        stream = null;
      }
      if (ctxRef?.processVideoEl) ctxRef.processVideoEl.srcObject = null;
      if (ctxRef?.videoEl) ctxRef.videoEl.srcObject = null;
      if (ctxRef?.canvasEl) {
        const c = ctxRef.canvasEl.getContext("2d");
        c?.clearRect(0, 0, ctxRef.canvasEl.width, ctxRef.canvasEl.height);
      }
      lastStatus = "stopped";
      emit({ type: "status", message: "Gesture engine stopped", level: "info" });
      ctxRef = null;
    },

    async health() {
      return {
        ok: lastStatus === "running",
        detail: lastStatus,
      };
    },
  };
}
